import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import { calculateAge } from '../utils/age';
import Card from '../components/Card';
import { useMobile } from '../context/MobileContext';

// 영상 URL 렌더링 헬퍼 (IIFE 대신 독립 함수로 분리 - Rolldown 파서 버그 우회)
function renderVideoEmbed(videoUrl) {
  if (!videoUrl) return null;
  const url = videoUrl;
  const ytWatch = 'youtube.com/watch';
  const ytShort = 'youtu.be/';
  const ytShorts = 'youtube.com/shorts/';
  
  if (url.includes(ytWatch) || url.includes(ytShort) || url.includes(ytShorts)) {
    let videoId = '';
    if (url.includes(ytShorts)) {
      videoId = url.split(ytShorts)[1].split('?')[0];
    } else if (url.includes(ytShort)) {
      videoId = url.split(ytShort)[1].split('?')[0];
    } else {
      videoId = new URLSearchParams(url.split('?')[1]).get('v');
    }
    
    // 자동재생(autoplay) 및 음소거(mute) 필수 (브라우저 정책에 의해 자동재생은 음소거 시에만 동작)
    const embedSrc = `https://www.youtube.com/embed/${videoId}?autoplay=1&mute=1&loop=1&playlist=${videoId}`;
    return (
      <div style={{ position: 'relative', paddingBottom: '56.25%', height: 0, overflow: 'hidden', borderRadius: '12px' }}>
        <iframe
          src={embedSrc}
          style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }}
          frameBorder="0"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
          title="YouTube video"
        />
      </div>
    );
  }

  const externalHref = url.startsWith('http') ? url : ('https://' + url);
  return (
    <a
      href={externalHref}
      target="_blank"
      rel="noreferrer"
      style={{ display: 'inline-block', padding: '12px 25px', backgroundColor: '#e1306c', color: 'white', fontWeight: 'bold', borderRadius: '8px', textDecoration: 'none' }}
    >
      인스타그램 {"&"} 영상 보러가기 👉
    </a>
  );
}

// 가격 표시 헬퍼
function renderPrice(dog) {
  const mainPrice = dog.price;
  const origPrice = dog.original_price;
  const formatP = (p) => {
    if (p === 0 || p === '0' || p === '무료분양' || p === '0만원') return '무료분양';
    if (typeof p === 'number') return p + '만원';
    if (typeof p === 'string' && !p.includes('만원') && p !== '무료분양') return p + '만원';
    return p;
  };
  const formattedMain = formatP(mainPrice);
  if (origPrice && mainPrice && origPrice > mainPrice && mainPrice !== 0 && mainPrice !== '무료분양') {
    const discountRate = Math.round(((origPrice - mainPrice) / origPrice) * 100);
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span style={{ fontSize: '1rem', color: '#aaa', textDecoration: 'line-through', fontWeight: '500' }}>{formatP(origPrice)}</span>
          <span style={{ fontSize: '1.1rem', color: '#FF4757', fontWeight: '800' }}>{discountRate}% 할인</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <span style={{ fontSize: '2rem' }}>{formattedMain}</span>
          {!!dog.is_negotiable && <span style={{ fontSize: '0.9rem', backgroundColor: '#eefbe7', color: '#7ed321', padding: '3px 10px', borderRadius: '15px', fontWeight: 'bold' }}>협의가능</span>}
        </div>
      </div>
    );
  }
  return (
    <div style={{ display: 'flex', alignItems: 'center' }}>
      {formattedMain}
      {!!dog.is_negotiable && mainPrice !== 0 && mainPrice !== '무료분양' && (
        <span style={{ fontSize: '0.9rem', backgroundColor: '#eefbe7', color: '#7ed321', padding: '3px 10px', borderRadius: '15px', marginLeft: '10px', fontWeight: 'bold' }}>협의가능</span>
      )}
    </div>
  );
}

const DetailPage = () => {
  const { isMobile } = useMobile();
  const location = useLocation();
  const navigate = useNavigate();
  const [dog, setDog] = useState(location.state?.dog || null); // Card에서 넘겨받은 강아지 정보 (없을 경우 fetch)
  
  const [currentUser, setCurrentUser] = useState(null);
  const [sellerInfo, setSellerInfo] = useState(null);
  const [activeDogCount, setActiveDogCount] = useState(0);
  const [loading, setLoading] = useState(!dog);

  // DB 리뷰 상태 관리
  const [storeReviews, setStoreReviews] = useState([]);
  const [hasReviewedThisDog, setHasReviewedThisDog] = useState(false);
  const [isLiked, setIsLiked] = useState(false);
  const [engagement, setEngagement] = useState({ likes: 0, views: 0 });

  // 신고 상태 관리
  const [showReportModal, setShowReportModal] = useState(false);
  const [reportData, setReportData] = useState({
    reason: '',
    details: ''
  });
  const reportReasons = ['허위 게시물', '내용이 다름', '추가요금', '완료', '기타입력'];

  // 리뷰 폼 state 추가
  const [showReviewForm, setShowReviewForm] = useState(false);
  const [reviewData, setReviewData] = useState({
    rating: 5,
    content: '',
    tags: []
  });
  const reviewTagsList = ['허위등록 없음', '친절해요.', '설명문 그대로예요.', '방문했는데 깔끔해요.'];

  // 유사 강아지 추천 관련
  const [recommendDogs, setRecommendDogs] = useState([]);

  const fetchRecommendations = async (currentDog) => {
    try {
      // 1. 동일 품종의 분양가능 게시글 조회
      const { data: sameBreed } = await api.dogs.getList({
        breed: currentDog.breed,
        exclude_id: currentDog.id,
        status: 'available',
        limit: 4
      });

      let finalDogs = sameBreed || [];

      // 2. 만약 4개 미만이라면, 최근 전체 게시글로 채우기
      if (finalDogs.length < 4) {
        const { data: recentDogs } = await api.dogs.getList({
          exclude_id: currentDog.id,
          status: 'available',
          limit: 10
        });

        if (recentDogs) {
          const idsInFinal = new Set(finalDogs.map(d => d.id));
          for (let d of recentDogs) {
            if (!idsInFinal.has(d.id)) {
              finalDogs.push(d);
              if (finalDogs.length >= 4) break;
            }
          }
        }
      }

      setRecommendDogs(finalDogs.slice(0, 4));
    } catch (e) {
      console.error('Failed to fetch recommendations:', e);
    }
  };

  const fetchEngagementStats = async (dogId) => {
    try {
      const { data, error } = await api.dogs.getDetail(dogId);
      if (!error && data) {
        setEngagement(prev => ({
          ...prev,
          likes: data.bookmarks_count || 0,
          views: Math.max(prev.views, data.views_count || 0)
        }));
      }
    } catch (err) {
      console.error('Failed to fetch engagement stats:', err);
    }
  };

  const fetchSellerInfo = async (sellerId) => {
    try {
      const { data, error } = await api.store.getProfile(sellerId);
      if (!error && data) {
        setSellerInfo({
          ...data.profile,
          business_name: data.business?.business_name || null,
          biz_no: data.business?.biz_no || null,
          animal_sale_no: data.business?.animal_sale_no || null
        });
        setActiveDogCount(data.active_count || 0);
        setStoreReviews(data.reviews || []);
      }
    } catch (err) {
      console.error('Failed to fetch seller info:', err);
    }
  };

  const checkLikeStatus = async (dogId) => {
    try {
      const { data } = await api.bookmarks.check(dogId);
      if (data && data.bookmarked) {
        setIsLiked(true);
      }
    } catch (e) {
      console.error('Failed to check like status:', e);
    }
  };

  useEffect(() => {
    if (dog) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      fetchRecommendations(dog);
    }
  }, [dog?.id]);

  // 다중 이미지 배열화
  const allImages = dog ? [dog.image_url || dog.image, ...(dog.additional_images || [])].filter(Boolean) : [];
  const [selectedImage, setSelectedImage] = useState(allImages.length > 0 ? allImages[0] : 'https://images.unsplash.com/photo-1516734212186-a967f81ad0d7?auto=format&fit=crop&q=80&w=800');

  useEffect(() => {
    const initPage = async () => {
      // 1. URL 체크 및 강아지 정보 로드
      const searchParams = new URLSearchParams(location.search);
      const dogId = searchParams.get('id');
      let currentDogId = dog?.id || dogId;

      if (!dog && dogId) {
        setLoading(true);
        const { data, error } = await api.dogs.getDetail(dogId);
        if (!error && data) {
          setDog(data);
          currentDogId = data.id;
          checkLikeStatus(data.id);
        } else {
          alert('존재하지 않거나 삭제된 게시물입니다.');
          navigate('/');
          return;
        }
        setLoading(false);
      } else if (dog) {
        setLoading(false);
        checkLikeStatus(dog.id);
      }

      // 2. 조회수 기록 - 완료 후 로컬 숫자 즉시 반영
      if (currentDogId) {
        try {
          const { data: user } = await api.auth.getUser();
          setCurrentUser(user);
          
          const { error: insError } = await api.analytics.logActivity(currentDogId, dog?.breed || '', 'view');
          
          if (!insError) {
            setEngagement(prev => ({ ...prev, views: prev.views + 1 }));
          }
        } catch (e) { console.error(e); }

        // 3. 통계 데이터 호출
        fetchEngagementStats(currentDogId);
      }
    };

    initPage();
    window.scrollTo(0, 0);
  }, [location.search]);

  useEffect(() => {
    if (dog) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      if (dog.seller_id) fetchSellerInfo(dog.seller_id);
    }
  }, [dog]);

  // Hook 선언을 모두 마친 후 조건부 렌더링을 수행해야 합니다. (React Hook 규칙)



  const toggleLike = async () => {
    if (!currentUser) {
      alert('관심아이 등록을 위해 먼저 로그인해 주세요!');
      navigate('/login');
      return;
    }

    try {
      const { data, error } = await api.bookmarks.toggle(dog.id);
      if (!error && data) {
        setIsLiked(data.bookmarked);
        setEngagement(prev => ({
          ...prev,
          likes: data.bookmarked ? prev.likes + 1 : Math.max(0, prev.likes - 1)
        }));
      }
    } catch (e) {
      console.error('Toggle like failed:', e);
    }
  };

  const handleStartChat = async () => {
    if (!currentUser) {
      alert('상담을 위해 먼저 로그인해 주세요!');
      navigate('/login');
      return;
    }

    if (currentUser.id === dog?.seller_id) {
      alert('본인의 등록글에는 상담할 수 없습니다.');
      return;
    }

    try {
      const targetSellerId = dog?.seller_id || '00000000-0000-0000-0000-000000000000';
      const { data, error } = await api.chat.createRoom(targetSellerId, currentUser.id, dog.id);
      if (!error && data) {
        navigate('/mypage', { state: { activeTab: 'chats', openRoomId: data.room_id } });
      } else {
        alert('채팅방 생성에 실패했습니다: ' + (error || '알 수 없는 오류'));
      }
    } catch (e) {
      alert('채팅방 생성 중 오류 발생: ' + e.message);
    }
  };

  const getRegionName = (address) => {
    if (!address) return '미등록';
    const regions = [
      "서울시", "인천시", "경기도", "부산시", "대구시", "대전시", "광주시", "울산시", 
      "강원도", "충청남도", "충청북도", "경상남도", "경상북도", "전라남도", "전라북도", "제주도", "세종시"
    ]; // 사용자 허용 지역 목록
    
    // address의 앞부분만 잘라서 비교 (예: '서울특별시 강남구' -> '서울시')
    const addStr = address.replace('특별시', '시').replace('광역시', '시');
    for (let r of regions) {
      if (addStr.includes(r.replace('특별시', '').replace('광역시', ''))) return r;
    }
    // 매칭 안될 시 띄어쓰기 기준 첫 단어
    return address.split(' ')[0];
  };

  const handleReviewTagToggle = (tag) => {
    setReviewData(prev => {
      const newTags = prev.tags.includes(tag) 
        ? prev.tags.filter(t => t !== tag)
        : [...prev.tags, tag];
      return { ...prev, tags: newTags };
    });
  };

  useEffect(() => {
    if (currentUser && storeReviews.length > 0 && dog) {
      const alreadyReviewed = storeReviews.some(r => r.dog_id === dog.id && r.reviewer_id === currentUser.id);
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setHasReviewedThisDog(alreadyReviewed);
    }
  }, [currentUser, storeReviews, dog?.id]);

  // 강아지 정보가 로드되면 선택된 이미지 초기화
  useEffect(() => {
    if (dog) {
      const firstImg = [dog.image_url || dog.image, ...(dog.additional_images || [])].filter(Boolean)[0];
      // eslint-disable-next-line react-hooks/set-state-in-effect
      if (firstImg) setSelectedImage(firstImg);
    }
  }, [dog]);

  if (loading) return <div style={{ padding: '100px', textAlign: 'center' }}>상세 정보를 불러오는 중입니다...</div>;
  if (!dog) return <div style={{ padding: '100px', textAlign: 'center' }}>게시물을 찾을 수 없습니다.</div>;

  const handleSubmitReview = async () => {
    if (!currentUser) {
      alert('리뷰 작성을 위해 먼저 로그인해 주세요!');
      navigate('/login');
      return;
    }
    
    if (hasReviewedThisDog) {
      alert('이미 이 게시물에 평가를 남기셨습니다.');
      return;
    }
    
    if (reviewData.content.length > 200) {
      return alert('리뷰 내용은 최대 200자까지만 작성 가능합니다.');
    }

    try {
      const { error } = await api.store.createReview({
        dog_id: dog.id,
        seller_id: dog.seller_id,
        rating: reviewData.rating,
        content: reviewData.content,
        tags: reviewData.tags
      });

      if (error) {
        if (error.includes('이미 이 게시물에 평가')) {
          alert('이미 이 게시물에 평가를 남기셨습니다.');
          setHasReviewedThisDog(true);
          setShowReviewForm(false);
        } else {
          alert('리뷰 등록 중 오류가 발생했습니다: ' + error);
        }
      } else {
        alert('소중한 리뷰가 등록되었습니다!');
        setShowReviewForm(false);
        setReviewData({ rating: 5, content: '', tags: [] });
        setHasReviewedThisDog(true);
        
        const newReviewEnriched = {
          id: Date.now(),
          dog_id: dog.id,
          seller_id: dog.seller_id,
          reviewer_id: currentUser.id,
          rating: reviewData.rating,
          content: reviewData.content,
          tags: reviewData.tags,
          created_at: new Date().toISOString(),
          reviewer_nickname: currentUser.nickname || '사용자',
          reviewer_image: currentUser.profile_image || 'https://plus.unsplash.com/premium_photo-1678197937465-bdbc4ed95815?auto=format&fit=crop&q=80&w=40&h=40'
        };
        setStoreReviews(prev => [...prev, newReviewEnriched]);
      }
    } catch (err) {
      alert('리뷰 등록 오류: ' + err.message);
    }
  };

  if (!dog) {
    return (
      <div className="container" style={{ padding: '100px 0', textAlign: 'center' }}>
        <h3>올바르지 않은 접근입니다.</h3>
        <button onClick={() => navigate('/')} style={{ marginTop: '20px' }}>홈으로 가기</button>
      </div>
    );
  }

  // 통계 계산
  const reviewStats = [
    { label: '허위등록 없음', color: 'var(--p4)', count: storeReviews.filter(r => r.tags.includes('허위등록 없음')).length },
    { label: '친절해요.', color: 'var(--p2)', count: storeReviews.filter(r => r.tags.includes('친절해요.')).length },
    { label: '설명문 그대로예요.', color: 'var(--primary)', count: storeReviews.filter(r => r.tags.includes('설명문 그대로예요.')).length },
    { label: '방문했는데 깔끔해요.', color: 'var(--p5)', count: storeReviews.filter(r => r.tags.includes('방문했는데 깔끔해요.')).length },
  ];
  const handleSubmitReport = async () => {
    if (!currentUser) {
      alert('신고를 위해 먼저 로그인해 주세요.');
      navigate('/login');
      return;
    }
    if (!reportData.reason) {
      return alert('신고 사유를 선택해주세요.');
    }
    if (reportData.reason === '기타입력' && !reportData.details.trim()) {
      return alert('기타입력의 경우 상세 내용을 기재해주세요.');
    }

    try {
      const { error: reportError } = await api.reports.create(
        dog.id,
        reportData.reason,
        reportData.details
      );

      if (reportError) {
        alert('신고 접수 중 오류가 발생했습니다: ' + reportError);
        return;
      }

      alert('신고가 정상적으로 접수되었으며, 사실 확인 후 조치하겠습니다.');
      setShowReportModal(false);
      setReportData({ reason: '', details: '' });
    } catch (e) {
      alert('신고 처리 오류: ' + e.message);
    }
  };

  const maxCount = Math.max(...reviewStats.map(s => s.count), 1);
  const totalReviews = storeReviews.length;
  const avgRating = totalReviews > 0 ? (storeReviews.reduce((sum, r) => sum + r.rating, 0) / totalReviews).toFixed(1) : '0.0';



  return (
    <div className="fade-in" style={{ paddingBottom: isMobile ? '80px' : '100px', backgroundColor: isMobile ? '#f8fafc' : 'transparent' }}>
      <div className="container" style={{ maxWidth: isMobile ? '100%' : '960px', padding: isMobile ? '0' : '0 20px' }}>
        
        {!isMobile && (
          <div style={{ padding: '20px 0', fontSize: '0.9rem', color: 'var(--muted-text)' }}>
             홈 &gt; 반려견 정보 &gt; <b>{dog.breed}</b>
          </div>
        )}

        {isMobile ? (
          // ==================== 모바일 전용 레이아웃 ====================
          <div style={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
            {/* 메인 이미지 슬라이더 */}
            <div style={{ position: 'relative', width: '100%', aspectRatio: '1/1', backgroundColor: '#000', overflow: 'hidden' }}>
              <img src={selectedImage} alt="Dog" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              
              {/* 이미지 뱃지 (분양상태 등) */}
              <div style={{
                position: 'absolute',
                top: '15px',
                left: '15px',
                backgroundColor: 'rgba(0,0,0,0.6)',
                color: 'white',
                padding: '4px 10px',
                borderRadius: '20px',
                fontSize: '0.75rem',
                fontWeight: 'bold',
                backdropFilter: 'blur(4px)'
              }}>
                {dog.status === 'available' ? '🔥 등록됨' : '🎉 완료'}
              </div>
            </div>

            {/* 멀티 썸네일 가로 스크롤 */}
            {allImages.length > 1 && (
              <div style={{ display: 'flex', gap: '8px', padding: '12px 15px', overflowX: 'auto', backgroundColor: '#fff', borderBottom: '1px solid #f1f5f9' }}>
                {allImages.map((img, idx) => (
                  <img 
                    key={idx} 
                    src={img} 
                    alt={`썸네일 ${idx + 1}`} 
                    onClick={() => setSelectedImage(img)}
                    style={{ 
                      width: '56px', height: '56px', objectFit: 'cover', borderRadius: '8px', cursor: 'pointer', flexShrink: 0,
                      border: selectedImage === img ? '2px solid var(--primary)' : '1px solid #e2e8f0',
                      opacity: selectedImage === img ? 1 : 0.6,
                      transition: 'all 0.2s'
                    }} 
                  />
                ))}
              </div>
            )}

            {/* 기본 상세 타이틀 카드 */}
            <div style={{ backgroundColor: 'white', padding: '20px 15px', marginBottom: '10px', borderBottom: '1px solid #e2e8f0' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
                <span style={{ fontSize: '0.85rem', color: 'var(--primary)', fontWeight: 'bold' }}>{dog.breed}</span>
                <button 
                  onClick={toggleLike}
                  style={{ 
                    background: 'none',
                    border: 'none',
                    fontSize: '1.4rem', 
                    cursor: 'pointer',
                    color: isLiked ? '#ff4757' : '#cbd5e1',
                    padding: 0,
                    lineHeight: 1
                  }}
                >
                  {isLiked ? '❤️' : '🤍'}
                </button>
              </div>

              <h1 style={{ fontSize: '1.4rem', fontWeight: '800', color: '#1e293b', marginBottom: '12px' }}>
                {dog.nickname}
              </h1>

              <div style={{ fontSize: '1.5rem', color: 'var(--primary-dark)', fontWeight: '800', marginBottom: '15px' }}>
                {renderPrice(dog)}
              </div>

              <div style={{ display: 'flex', gap: '15px', fontSize: '0.78rem', color: '#64748b', borderTop: '1px solid #f1f5f9', paddingTop: '12px' }}>
                <span>관심 {engagement.likes}명</span>
                <span>조회 {engagement.views}회</span>
                <span style={{ marginLeft: 'auto' }}>등록일: {new Date(dog.created_at).toLocaleDateString()}</span>
              </div>
            </div>

            {/* 꿀팁 정보 알림 */}
            <div style={{ 
              margin: '0 10px 10px 10px', 
              padding: '12px 15px', 
              backgroundColor: '#eff6ff', 
              borderRadius: '8px', 
              fontSize: '0.8rem', 
              color: '#1e40af', 
              lineHeight: '1.4',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              <span>💡</span>
              <span>상담 시 <b>"팔도댕댕 보고 연락드렸어요"</b> 라고 하시면 친절하고 빠른 안내를 받아보실 수 있습니다.</span>
            </div>

            {/* 강아지 프로필 카드 형식 스펙 그리드 */}
            <div style={{ backgroundColor: 'white', padding: '20px 15px', marginBottom: '10px', borderBottom: '1px solid #e2e8f0' }}>
              <h3 style={{ fontSize: '1rem', fontWeight: '800', marginBottom: '15px', color: '#1e293b' }}>🐶 강아지 기본 정보</h3>
              
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <div style={{ backgroundColor: '#f8fafc', padding: '10px 12px', borderRadius: '8px' }}>
                  <div style={{ fontSize: '0.72rem', color: '#64748b', marginBottom: '4px' }}>성별</div>
                  <div style={{ fontSize: '0.88rem', fontWeight: '700', color: '#334155' }}>{dog.gender === '남아' ? '♂️ 남아' : '♀️ 여아'}</div>
                </div>
                <div style={{ backgroundColor: '#f8fafc', padding: '10px 12px', borderRadius: '8px' }}>
                  <div style={{ fontSize: '0.72rem', color: '#64748b', marginBottom: '4px' }}>나이 (개월수)</div>
                  <div style={{ fontSize: '0.88rem', fontWeight: '700', color: '#334155' }}>{calculateAge(dog.birthday, dog.age)}</div>
                </div>
                <div style={{ backgroundColor: '#f8fafc', padding: '10px 12px', borderRadius: '8px' }}>
                  <div style={{ fontSize: '0.72rem', color: '#64748b', marginBottom: '4px' }}>접종 상태</div>
                  <div style={{ fontSize: '0.88rem', fontWeight: '700', color: '#334155' }}>{dog.vaccine || '미등록'}</div>
                </div>
                <div style={{ backgroundColor: '#f8fafc', padding: '10px 12px', borderRadius: '8px' }}>
                  <div style={{ fontSize: '0.72rem', color: '#64748b', marginBottom: '4px' }}>등록 지역</div>
                  <div style={{ fontSize: '0.88rem', fontWeight: '700', color: '#334155' }}>{dog.region}</div>
                </div>
              </div>
            </div>

            {/* 판매 스토어 정보 */}
            {sellerInfo && (
              <div style={{ backgroundColor: 'white', padding: '20px 15px', marginBottom: '10px', borderBottom: '1px solid #e2e8f0' }}>
                <h3 style={{ fontSize: '1rem', fontWeight: '800', marginBottom: '15px', color: '#1e293b' }}>🏪 안심 파트너 매장 정보</h3>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '15px' }}>
                  <div style={{ 
                    width: '48px', height: '48px', borderRadius: '50%', backgroundColor: 'var(--primary-light)', 
                    display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.5rem' 
                  }}>
                    🏪
                  </div>
                  <div>
                    <div style={{ fontSize: '0.95rem', fontWeight: '700', color: '#1e293b' }}>
                      {sellerInfo.business_name || sellerInfo.nickname || '인증 스토어'}
                    </div>
                    <div style={{ fontSize: '0.75rem', color: '#64748b', marginTop: '2px' }}>
                      현재 등록 {activeDogCount}건 &nbsp;|&nbsp; 완료 건수 {sellerInfo.completed_adoption_count || 0}건
                    </div>
                  </div>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', fontSize: '0.8rem', borderTop: '1px solid #f1f5f9', paddingTop: '15px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ color: '#64748b' }}>매장 주소</span>
                    <span style={{ fontWeight: '500', color: '#334155' }}>{sellerInfo.store_address || '미등록'}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ color: '#64748b' }}>매장 연락처</span>
                    <span style={{ fontWeight: '500', color: '#334155' }}>{sellerInfo.store_contact || '미등록'}</span>
                  </div>
                  {sellerInfo.animal_sale_no && (
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span style={{ color: '#64748b' }}>동물판매업등록번호</span>
                      <span style={{ fontWeight: '500', color: '#334155', fontSize: '0.75rem' }}>{sellerInfo.animal_sale_no}</span>
                    </div>
                  )}
                </div>

                <button 
                  onClick={() => navigate(`/store/${dog.seller_id}`)} 
                  style={{
                    width: '100%', padding: '10px 0', border: '1px solid #e2e8f0', borderRadius: '8px',
                    backgroundColor: 'white', color: '#475569', fontSize: '0.85rem', fontWeight: '600',
                    cursor: 'pointer', marginTop: '15px'
                  }}
                >
                  🏪 이 매장의 등록글 전체 보기
                </button>
              </div>
            )}

            {/* 상세 설명 */}
            <div style={{ backgroundColor: 'white', padding: '20px 15px', marginBottom: '10px', borderBottom: '1px solid #e2e8f0' }}>
              <h3 style={{ fontSize: '1rem', fontWeight: '800', marginBottom: '12px', color: '#1e293b' }}>📄 상세 설명</h3>
              <div 
                className="quill-content"
                style={{ color: '#475569', fontSize: '0.98rem', lineHeight: '1.6' }}
                dangerouslySetInnerHTML={{ 
                  __html: dog.desc || `<p>안녕하세요! 팔도댕댕 인증 매장입니다.<br/>사랑스런 ${dog.breed} 아이 정보를 소개합니다.<br/>성격이 매우 온순하고 사회성이 좋습니다.<br/>궁금하신 점은 언제든 상담 신청해주세요.</p>` 
                }} 
              />

              {dog.video_url && (
                <div style={{ marginTop: '20px', borderTop: '1px solid #f1f5f9', paddingTop: '20px' }}>
                  <h4 style={{ fontSize: '0.9rem', fontWeight: '800', marginBottom: '12px', color: 'var(--primary-dark)' }}>🎥 생생한 소개 영상</h4>
                  {renderVideoEmbed(dog.video_url)}
                </div>
              )}
            </div>

            {/* 매장 리뷰 및 신뢰 평가 */}
            <div style={{ backgroundColor: 'white', padding: '20px 15px', marginBottom: '10px', borderBottom: '1px solid #e2e8f0' }}>
              <h3 style={{ fontSize: '1rem', fontWeight: '800', marginBottom: '15px', color: '#1e293b' }}>⭐ 매장 안심 평점</h3>
              <div style={{ display: 'flex', alignItems: 'center', gap: '15px', marginBottom: '20px' }}>
                <div style={{ fontSize: '2rem', fontWeight: '900', color: '#1e293b' }}>{avgRating}</div>
                <div>
                  <div style={{ color: '#FFD54F', fontSize: '1.1rem' }}>
                    {'★'.repeat(Math.round(parseFloat(avgRating)))}
                    {'☆'.repeat(5 - Math.round(parseFloat(avgRating)))}
                  </div>
                  <div style={{ fontSize: '0.75rem', color: '#64748b', marginTop: '2px' }}>총 {totalReviews}건의 리뷰 평점</div>
                </div>
              </div>

              {/* 매장 키워드 리뷰 통계 */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: '20px' }}>
                {reviewStats.map((stat, i) => (
                  <div key={i}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px', fontSize: '0.78rem', color: '#475569' }}>
                      <span>{stat.label}</span>
                      <span style={{ fontWeight: '700' }}>{stat.count}명</span>
                    </div>
                    <div style={{ height: '6px', backgroundColor: '#f1f5f9', borderRadius: '3px', overflow: 'hidden' }}>
                      <div style={{ width: `${(stat.count / maxCount) * 100}%`, height: '100%', backgroundColor: stat.color }} />
                    </div>
                  </div>
                ))}
              </div>

              {/* 매장 텍스트 리뷰 목록 */}
              {storeReviews.length > 0 && (
                <div style={{ borderTop: '1px solid #f1f5f9', paddingTop: '15px' }}>
                  <h4 style={{ fontSize: '0.85rem', fontWeight: '800', marginBottom: '12px', color: '#334155' }}>최근 한줄평</h4>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    {storeReviews.slice(-2).reverse().map((r, idx) => (
                      <div key={idx} style={{ fontSize: '0.8rem', backgroundColor: '#f8fafc', padding: '10px 12px', borderRadius: '8px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '6px' }}>
                          <span style={{ fontWeight: 'bold', color: '#1e293b' }}>{r.reviewer_nickname}</span>
                          <span style={{ color: '#FFD54F', fontSize: '0.75rem' }}>{'★'.repeat(r.rating)}</span>
                        </div>
                        <div style={{ color: '#475569', lineHeight: '1.4' }}>{r.content || '평점 및 태그 리뷰를 남겨주셨습니다.'}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* 리뷰 작성 & 신고 버튼 */}
              {(!currentUser || currentUser.id !== dog?.seller_id) && (
                <div style={{ display: 'flex', gap: '8px', marginTop: '15px' }}>
                  {!currentUser && (
                    <button 
                      onClick={() => {
                        alert('로그인하셔야 가능합니다.');
                        navigate('/login');
                      }} 
                      style={{ 
                        flex: 1, padding: '10px 0', border: '1px solid var(--primary)', borderRadius: '8px',
                        backgroundColor: 'white', color: 'var(--primary)',
                        fontSize: '0.82rem', fontWeight: '700', cursor: 'pointer'
                      }}
                    >
                      ⭐ 매장 방문/상담 리뷰 남기기
                    </button>
                  )}
                  <button 
                    onClick={() => setShowReportModal(true)}
                    style={{ 
                      flex: currentUser ? 1 : undefined,
                      padding: '10px 12px', border: '1px solid #f87171', borderRadius: '8px',
                      backgroundColor: 'white', color: '#ef4444', fontSize: '0.82rem', fontWeight: '700', cursor: 'pointer'
                    }}
                  >
                    🚨 신고
                  </button>
                </div>
              )}
            </div>
          </div>
        ) : (
          // ==================== 데스크탑 레이아웃 (기존 유지) ====================
          <div className="detail-main-grid" style={{ width: '100%' }}>
            <div style={{ minWidth: 0 }}>
              <div style={{ borderRadius: 'var(--border-radius)', overflow: 'hidden', marginBottom: '15px', boxShadow: 'var(--shadow)' }}>
                <img src={selectedImage} alt="Dog" style={{ width: '100%', height: 'auto', maxHeight: '400px', aspectRatio: '4/3', objectFit: 'cover' }} />
              </div>

              <div style={{ 
                marginBottom: '15px', 
                padding: '12px', 
                backgroundColor: '#f1f3f5', 
                borderRadius: '10px', 
                fontSize: '0.85rem', 
                color: '#495057', 
                textAlign: 'center',
                lineHeight: '1.5'
              }}>
                문의하실 때는 <b style={{ color: 'var(--primary)' }}>'팔도댕댕에서 보고 전화드렸습니다.'</b>라고 말씀하시면 문의가 쉬워집니다.
              </div>
              
              {allImages.length > 1 && (
                <div style={{ display: 'flex', gap: '8px', marginBottom: '20px', overflowX: 'auto', paddingBottom: '8px' }}>
                  {allImages.map((img, idx) => (
                    <img 
                      key={idx} 
                      src={img} 
                      alt={`썸네일 ${idx + 1}`} 
                      onClick={() => setSelectedImage(img)}
                      style={{ 
                        width: '60px', height: '60px', objectFit: 'cover', borderRadius: '8px', cursor: 'pointer', flexShrink: 0,
                        border: selectedImage === img ? '3px solid var(--primary)' : '1px solid #ddd',
                        opacity: selectedImage === img ? 1 : 0.6,
                        transition: 'all 0.2s'
                      }} 
                    />
                  ))}
                </div>
              )}
              
              <div className="glass-card" style={{ padding: '20px', marginBottom: '30px' }}>
                <h2 style={{ marginBottom: '20px' }}>상세 설명</h2>
                <p style={{ whiteSpace: 'pre-wrap', color: 'var(--muted-text)', fontSize: '1.02rem', lineHeight: '1.6', marginBottom: dog.video_url ? '30px' : '0' }}>
                  {dog.desc || `안녕하세요! 팔도댕댕 인증 매장입니다.\n사랑스런 ${dog.breed} 아이 정보를 소개합니다.\n성격이 매우 온순하고 사회성이 좋습니다.\n궁금하신 점은 언제든 상담 신청해주세요.`}
                </p>
                
                {dog.video_url && (
                  <div style={{ marginTop: '20px', borderTop: '1px solid #eee', paddingTop: '20px' }}>
                    <h3 style={{ fontSize: '1.2rem', marginBottom: '15px', color: 'var(--primary-dark)' }}>🎥 생생한 영상 확인</h3>
                    {renderVideoEmbed(dog.video_url)}
                  </div>
                )}
              </div>

              <div className="glass-card" style={{ padding: '20px' }}>
                <h2 style={{ marginBottom: '25px' }}>판매자 신뢰 평가</h2>
                <div className="info-grid-2">
                  <div>
                    {reviewStats.map((stat, i) => (
                      <div key={i} style={{ marginBottom: '15px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '5px', fontSize: '0.9rem' }}>
                          <span>{stat.label}</span>
                          <span style={{ fontWeight: '700' }}>{stat.count}</span>
                        </div>
                        <div style={{ height: '8px', backgroundColor: '#eee', borderRadius: '4px', overflow: 'hidden' }}>
                          <div style={{ width: `${(stat.count / maxCount) * 100}%`, height: '100%', backgroundColor: stat.color }} />
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="info-grid-child-border">
                    <h4 style={{ marginBottom: '15px' }}>최근 리뷰</h4>
                    <div style={{ fontSize: '0.9rem', color: '#666' }}>최근 등록된 리뷰가 {totalReviews}건 있습니다.</div>
                    
                    <div style={{ marginTop: '15px' }}>
                      {storeReviews.slice(-3).reverse().map((r, idx) => (
                        <div key={idx} style={{ marginBottom: '15px', fontSize: '0.85rem' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                            <img src={r.reviewer_image} alt="프로필" style={{ width: '24px', height: '24px', borderRadius: '50%', objectFit: 'cover' }} />
                            <span style={{ fontWeight: 'bold', color: '#333' }}>{r.reviewer_nickname}</span>
                            <span style={{ color: '#FFD54F', marginLeft: 'auto' }}>{'★'.repeat(r.rating)}</span>
                          </div>
                          <div style={{ color: '#444', lineHeight: '1.4' }}>{r.content || '텍스트 리뷰가 없습니다.'}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="detail-sidebar" style={{ position: 'sticky', top: '100px', alignSelf: 'start' }}>
              <div className="glass-card" style={{ padding: '20px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <h1 style={{ fontSize: '1.45rem', marginBottom: '5px', flex: 1 }}>{dog.nickname} ({dog.breed})</h1>
                  <button 
                    onClick={toggleLike}
                    style={{ 
                      background: isLiked ? '#fff1f2' : '#f8f9fa', 
                      border: isLiked ? '1px solid #ff4757' : '1px solid #ddd', 
                      fontSize: '0.85rem', 
                      cursor: 'pointer',
                      padding: '6px 12px', 
                      borderRadius: '25px',
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: '8px',
                      color: isLiked ? '#ff4757' : '#666',
                      fontWeight: '700',
                      transition: 'all 0.3s'
                    }}
                  >
                    {isLiked ? '❤️ 관심중' : '🤍 관심등록'}
                  </button>
                </div>
                
                <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '8px', fontSize: '0.85rem', color: '#888', gap: '5px' }}>
                  <span>관심 {engagement.likes}명</span>
                  <span> / </span>
                  <span>조회 {engagement.views}회</span>
                </div>

                <div style={{ fontSize: '0.95rem', color: '#666', marginBottom: '10px' }}>
                  판매자: <b>{sellerInfo?.business_name || sellerInfo?.nickname || '불러오는 중...'}</b>
                </div>
                <div style={{ fontSize: '0.85rem', marginBottom: '15px' }}>
                  <span style={{ backgroundColor: 'var(--primary-light)', color: 'var(--primary-dark)', padding: '4px 10px', borderRadius: '15px', fontWeight: 'bold' }}>
                    현재 등록 {activeDogCount} / 완료 {sellerInfo?.completed_adoption_count || 0}
                  </span>
                </div>

                <div style={{ fontSize: '1.3rem', color: 'var(--primary-dark)', fontWeight: '800', marginBottom: '15px' }}>
                  {renderPrice(dog)}
                </div>
                
                <div style={{ borderTop: '1px solid #eee', paddingTop: '15px', display: 'grid', gap: '10px', fontSize: '0.85rem' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ color: 'var(--muted-text)' }}>최초 등록일</span>
                    <span style={{ fontWeight: '500' }}>{new Date(dog.created_at).toLocaleDateString()}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ color: 'var(--muted-text)' }}>등록지역</span>
                    <span>{dog.region}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ color: 'var(--muted-text)' }}>생일 / 개월</span>
                    <span>{dog.birthday ? new Date(dog.birthday).toLocaleDateString() : '미등록'} / {calculateAge(dog.birthday, dog.age)}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ color: 'var(--muted-text)' }}>성별 / 접종</span>
                    <span>{dog.gender} / {dog.vaccine || '미등록'}</span>
                  </div>
                  {sellerInfo && (
                    <>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span style={{ color: 'var(--muted-text)' }}>스토어 연락처</span>
                        <span style={{ fontWeight: '500' }}>{sellerInfo.store_contact || '미등록'}</span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span style={{ color: 'var(--muted-text)' }}>매장 위치</span>
                        <span style={{ fontWeight: '500' }}>{getRegionName(sellerInfo.store_address)}</span>
                      </div>
                      {sellerInfo.biz_no && (
                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                          <span style={{ color: 'var(--muted-text)' }}>사업자번호</span>
                          <span style={{ fontWeight: '500', fontSize: '0.85rem' }}>{sellerInfo.biz_no}</span>
                        </div>
                      )}
                      {sellerInfo.animal_sale_no && (
                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                          <span style={{ color: 'var(--muted-text)' }}>동물판매번호</span>
                          <span style={{ fontWeight: '500', fontSize: '0.85rem' }}>{sellerInfo.animal_sale_no}</span>
                        </div>
                      )}
                    </>
                  )}
                   <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                     <span style={{ color: 'var(--muted-text)' }}>고객평점</span>
                     <span style={{ color: '#FFD54F' }}>
                       {totalReviews > 0 ? (
                         <>
                           {'★'.repeat(Math.round(parseFloat(avgRating)))}
                           {'☆'.repeat(5 - Math.round(parseFloat(avgRating)))}
                           {` (${avgRating})`}
                         </>
                       ) : (
                         <span style={{ color: 'var(--muted-text)', fontSize: '0.85rem' }}>평가 없음</span>
                       )}
                     </span>
                   </div>
                </div>

                <div className="desktop-only" style={{ marginTop: '20px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  <button onClick={handleStartChat} style={chatBtnStyle}>
                     💬 다잇톡으로 상담하기
                  </button>

                  {sellerInfo && (
                    <button onClick={() => navigate(`/store/${dog.seller_id}`)} style={{ ...chatBtnStyle, backgroundColor: '#f0f0f0', color: '#555' }}>
                      🏪 이 스토어의 모든 게시물 보기
                    </button>
                  )}
                  {(!currentUser || currentUser.id !== dog?.seller_id) && (
                    <>
                      {!currentUser && (
                        <button onClick={() => {
                          alert('로그인하셔야 가능합니다.');
                          navigate('/login');
                        }} style={{ ...chatBtnStyle, backgroundColor: '#fef2f2', color: '#e63946' }}>
                          ⭐ 리뷰 및 평점 남기기
                        </button>
                      )}
                      <div style={{ textAlign: 'right', marginTop: '10px' }}>
                        <span 
                          onClick={() => setShowReportModal(true)} 
                          style={{ fontSize: '0.85rem', color: '#888', textDecoration: 'underline', cursor: 'pointer' }}
                        >
                          🚨 이 게시물 신고하기
                        </span>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* 유사한 댕댕이 추천 섹션 */}
        {recommendDogs.length > 0 && (
          <div style={{ marginTop: '40px', borderTop: '1px solid #eee', paddingTop: '30px', paddingLeft: isMobile ? '15px' : '0', paddingRight: isMobile ? '15px' : '0' }}>
            <h3 style={{ fontSize: '1.25rem', fontWeight: '800', marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '8px' }}>
              🐾 이 아이와 비슷한 다른 댕댕이들
            </h3>
            <div className="recommend-grid">
              {recommendDogs.map((d) => (
                <Card 
                  key={d.id} 
                  type="small" 
                  data={{
                    ...d,
                    image: d.image_url || d.image || 'https://images.unsplash.com/photo-1543466835-00a7907e9de1?q=80&w=600&auto=format&fit=crop',
                    breed: d.breed || '견종 미상',
                    nickname: d.nickname || '이름 없음',
                    gender: d.gender || '-',
                    region: d.region || '지역 미지정',
                    age: calculateAge(d.birthday, d.age),
                    price: d.price,
                    date: new Date(d.created_at).toLocaleDateString()
                  }} 
                />
              ))}
            </div>
          </div>
        )}

        {isMobile && <div className="mobile-bottom-bar" style={{ position: 'fixed', bottom: 0, left: 0, right: 0, height: '64px', backgroundColor: '#ffffff', borderTop: '1px solid #e2e8f0', boxShadow: '0 -4px 12px rgba(0,0,0,0.05)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 15px', gap: '10px', zIndex: 1000 }}>
               <button 
                 onClick={() => {
                    const phone = sellerInfo?.store_contact || sellerInfo?.phone;
                    if(phone) window.location.href = `tel:${phone}`;
                    else alert('등록된 연락처가 없습니다.');
                 }} 
                 style={{ flex: 1, height: '46px', borderRadius: '10px', backgroundColor: 'white', color: 'var(--primary-dark)', border: '1px solid var(--primary)', fontWeight: '700', fontSize: '0.92rem', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}
               >
                 📞 전화 문의
               </button>
               <button onClick={handleStartChat} style={{ flex: 1.5, height: '46px', borderRadius: '10px', backgroundColor: 'var(--primary-dark)', color: 'white', border: 'none', fontWeight: '700', fontSize: '0.92rem', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}>
                 💬 다잇톡 상담
               </button>
            </div>}

            
            {showReportModal && currentUser?.id !== dog?.seller_id && (
              <div style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <div style={{ backgroundColor: 'white', padding: '30px', borderRadius: '15px', width: '90%', maxWidth: '400px', boxShadow: '0 10px 25px rgba(0,0,0,0.2)' }}>
                  <h3 style={{ marginBottom: '20px', color: '#e63946' }}>🚨 허위게시물/문제 게시물 신고</h3>
                  <p style={{ fontSize: '0.9rem', color: '#666', marginBottom: '20px' }}>신고 사유를 선택하시고 제출해주세요. <br />허위 신고 시 이용에 제한이 있을 수 있습니다.</p>
                  
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '20px' }}>
                    {reportReasons.map(r => (
                      <label key={r} style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                        <input 
                          type="radio" 
                          name="report_reason" 
                          value={r} 
                          checked={reportData.reason === r} 
                          onChange={(e) => setReportData({ ...reportData, reason: e.target.value })}
                        />
                        {r}
                      </label>
                    ))}
                  </div>

                  {reportData.reason === '기타입력' && (
                    <textarea 
                      placeholder="기타 신고 사유를 상세히 적어주세요." 
                      value={reportData.details}
                      onChange={(e) => setReportData({ ...reportData, details: e.target.value })}
                      style={{ width: '100%', height: '80px', padding: '10px', borderRadius: '8px', border: '1px solid #ddd', resize: 'none', marginBottom: '20px', fontSize: '0.9rem', outline: 'none' }}
                    />
                  )}

                  <div style={{ display: 'flex', gap: '10px' }}>
                    <button onClick={() => { setShowReportModal(false); setReportData({reason:'', details:''}); }} style={{ flex: 1, padding: '12px', borderRadius: '8px', backgroundColor: '#eee', color: '#333', border: 'none', cursor: 'pointer', fontWeight: 'bold' }}>취소</button>
                    <button onClick={handleSubmitReport} style={{ flex: 1, padding: '12px', borderRadius: '8px', backgroundColor: '#e63946', color: 'white', border: 'none', cursor: 'pointer', fontWeight: 'bold' }}>신고 접수</button>
                  </div>
                </div>
              </div>
            )}

            {currentUser && !hasReviewedThisDog && currentUser.id !== dog?.seller_id && (
              <div className="fade-in" style={{ marginTop: '20px', padding: '20px', backgroundColor: '#fdfdfd', border: '1px solid #eee', borderRadius: '12px' }}>
                <h4 style={{ marginBottom: '15px', color: 'var(--primary-dark)', fontSize: '1.1rem' }}>리뷰 작성하기</h4>
                
                
                <div style={{ marginBottom: '15px' }}>
                  <div style={{ fontSize: '0.9rem', color: '#666', marginBottom: '8px' }}>평점 선택</div>
                  <div style={{ display: 'flex', gap: '5px' }}>
                    {[1, 2, 3, 4, 5].map((star) => (
                      <span 
                        key={star} 
                        onClick={() => setReviewData({...reviewData, rating: star})}
                        style={{ fontSize: '1.5rem', cursor: 'pointer', color: star <= reviewData.rating ? '#FFD54F' : '#e0e0e0', transition: 'color 0.2s' }}
                      >
                        ★
                      </span>
                    ))}
                  </div>
                </div>

                
                <div style={{ marginBottom: '15px' }}>
                  <div style={{ fontSize: '0.9rem', color: '#666', marginBottom: '8px' }}>이 매장의 좋았던 점 (다중선택 가능)</div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                    {reviewTagsList.map(tag => (
                      <button
                        key={tag}
                        onClick={() => handleReviewTagToggle(tag)}
                        style={{
                          padding: '6px 12px', borderRadius: '20px', fontSize: '0.85rem', cursor: 'pointer', border: '1px solid', transition: 'all 0.2s',
                          backgroundColor: reviewData.tags.includes(tag) ? 'var(--primary)' : 'white',
                          color: reviewData.tags.includes(tag) ? 'white' : '#666',
                          borderColor: reviewData.tags.includes(tag) ? 'var(--primary)' : '#ddd',
                        }}
                      >
                        {tag}
                      </button>
                    ))}
                  </div>
                </div>

                
                <div style={{ marginBottom: '15px' }}>
                  <div style={{ fontSize: '0.9rem', color: '#666', marginBottom: '8px' }}>
                     상세 후기 <span style={{ fontSize: '0.8rem', float: 'right' }}>{reviewData.content.length} / 200자</span>
                  </div>
                  <textarea 
                    placeholder="매장 방문이나 상담 후기를 자유롭게 적어주세요!" 
                    maxLength={200}
                    value={reviewData.content}
                    onChange={(e) => setReviewData({...reviewData, content: e.target.value})}
                    style={{ width: '100%', height: '80px', padding: '10px', borderRadius: '8px', border: '1px solid #ddd', resize: 'none', fontSize: '0.9rem', outline: 'none' }}
                  />
                </div>

                <button 
                  onClick={handleSubmitReview}
                  disabled={reviewData.content.length === 0 && reviewData.tags.length === 0}
                  style={{ width: '100%', padding: '12px', borderRadius: '8px', backgroundColor: (reviewData.content.length === 0 && reviewData.tags.length === 0) ? '#ddd' : 'var(--primary-dark)', color: 'white', fontWeight: 'bold', border: 'none', cursor: 'pointer' }}
                >
                  리뷰 등록 완료
                </button>
              </div>
            )}
      </div>
    </div>
  );
};

const chatBtnStyle = { padding: '15px', borderRadius: '12px', backgroundColor: 'var(--primary-dark)', color: 'white', fontWeight: '700', fontSize: '1rem', border: 'none', cursor: 'pointer' };

export default DetailPage;
